import { Button } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import type { ReactNode } from "react";

import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import type { LayerInspectorMode } from "../../helpers/layerInspectorTools";
import { ReactComponent as ConnectIcon } from "../../icons-and-animations/layer-inspector/connect.svg";
import { ReactComponent as DesignIcon } from "../../icons-and-animations/layer-inspector/design.svg";
import { ReactComponent as ViewIcon } from "../../icons-and-animations/layer-inspector/view.svg";
import { useMst } from "../../stores/main";

const MODE_OPTIONS: Array<{
  mode: LayerInspectorMode;
  label: string;
  tooltip: string;
  icon: ReactNode;
}> = [
  {
    mode: "view",
    label: "View",
    tooltip:
      "Click interactive elements to trigger what viewers will see, like opening links or changing scenes.",
    icon: <ViewIcon />,
  },
  {
    mode: "design",
    label: "Design",
    tooltip:
      "Select and edit layers, visual properties, and timing, or create new layers.",
    icon: <DesignIcon />,
  },
  {
    mode: "connect",
    label: "Connect",
    tooltip: "Right-click layers to create or edit connectors.",
    icon: <ConnectIcon />,
  },
];

export const LayerInspectorModeSelector = observer(() => {
  const {
    layoutStore: {
      setConnectorsPanelCollapsed,
      setLayerVisualPanelCollapsed,
      setSchemaDrawerOpen,
    },
    modsStore: { setActiveMod },
    playerStore: {
      layerInspectorAvailable,
      layerInspectorMode,
      setLayerInspectorMode,
    },
    timelineStore,
  } = useMst();

  const selectMode = useCallback(
    (mode: LayerInspectorMode) => {
      setLayerInspectorMode(mode);

      if (mode === "view") {
        return;
      }

      if (mode === "design") {
        timelineStore.setCollapsed(false);
        setLayerVisualPanelCollapsed(false);
        setActiveMod(undefined);
        setConnectorsPanelCollapsed(true);
        return;
      }

      timelineStore.setCollapsed(true);
      setLayerVisualPanelCollapsed(true);
      setSchemaDrawerOpen(false);
      setConnectorsPanelCollapsed(false);
    },
    [
      setActiveMod,
      setConnectorsPanelCollapsed,
      setLayerInspectorMode,
      setLayerVisualPanelCollapsed,
      setSchemaDrawerOpen,
      timelineStore,
    ]
  );

  return (
    <div
      className="layer-inspector-mode-selector"
      role="radiogroup"
      aria-label="Player interaction mode"
    >
      {MODE_OPTIONS.map((option) => {
        const selected = layerInspectorMode === option.mode;

        return (
          <HoverTooltip
            key={option.mode}
            title={option.label}
            description={option.tooltip}
            placement="bottom"
          >
            <Button
              type="text"
              className={classNames("layer-inspector-mode-selector__button", {
                [`layer-inspector-mode-selector__button--${option.mode}`]: true,
                "layer-inspector-mode-selector__button--selected": selected,
              })}
              disabled={!layerInspectorAvailable}
              aria-label={`${option.label} mode`}
              aria-checked={selected}
              role="radio"
              onClick={() => selectMode(option.mode)}
            >
              <span className="layer-inspector-mode-selector__button-content">
                <span className="layer-inspector-mode-selector__icon">
                  {option.icon}
                </span>
                <span className="layer-inspector-mode-selector__label">
                  {option.label}
                </span>
              </span>
            </Button>
          </HoverTooltip>
        );
      })}
    </div>
  );
});
